import { __ } from "@wordpress/i18n";
import {
	InspectorControls,
	useBlockProps,
	MediaUpload,
	MediaUploadCheck,
} from "@wordpress/block-editor";
import {
	PanelBody,
	SelectControl,
	Spinner,
	ToggleControl,
	TextControl,
	Button,
} from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import { useState, useEffect } from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";

export default function Edit({ attributes, setAttributes }) {
	const {
		acfField,
		showLabel,
		customLabel,
		fallbackText,
		fieldType,
		fallbackImageId,
		fallbackImageUrl,
	} = attributes;

	const blockProps = useBlockProps();
	const [fields, setFields] = useState([]);
	const [loading, setLoading] = useState(true);
	const [preview, setPreview] = useState("");
	const [loadingPreview, setLoadingPreview] = useState(false);

	const { postId } = useSelect(
		(select) => ({
			postId: select("core/editor").getCurrentPostId(),
		}),
		[],
	);

	useEffect(() => {
		setLoading(true);
		apiFetch({ path: "/swift-meta-field/v1/all-fields" })
			.then((fetchedFields) => {
				const fieldOptions = fetchedFields.map((field) => ({
					...field,
					value: field.name,
					label: field.label_with_group,
				}));
				setFields([
					{ value: "", label: __("— Select a Field —", "swift-fse-blocks") },
					...fieldOptions,
				]);
			})
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		if (!acfField) {
			setPreview("");
			return;
		}
		setLoadingPreview(true);
		const previewUrl = `/swift-meta-field/v1/preview?field=${acfField}&post_id=${postId}`;
		apiFetch({ path: previewUrl })
			.then((res) => setPreview(res.formatted))
			.finally(() => setLoadingPreview(false));
	}, [acfField, postId]);

	const onFieldChange = (fieldName) => {
		const selectedField = fields.find((f) => f.name === fieldName);
		setAttributes({
			acfField: selectedField?.name || "",
			acfFieldLabel: selectedField?.label || "",
			fieldType: selectedField?.type || "text",
		});
	};

	// ** NEW: Fallback UI logic **
	const fallbackControls =
		fieldType === "image" ? (
			<div className="swift-fse-blocks-fallback-image">
				<MediaUploadCheck>
					<MediaUpload
						onSelect={(media) =>
							setAttributes({
								fallbackImageId: media.id,
								fallbackImageUrl: media.url,
							})
						}
						allowedTypes={["image"]}
						value={fallbackImageId}
						render={({ open }) => (
							<div>
								{fallbackImageUrl && (
									<img
										src={fallbackImageUrl}
										alt={__("Fallback Image", "swift-fse-blocks")}
										style={{
											maxWidth: "100%",
											height: "auto",
											marginBottom: "10px",
										}}
									/>
								)}
								<Button onClick={open} primary>
									{fallbackImageId
										? __("Change Fallback Image", "swift-fse-blocks")
										: __("Select Fallback Image", "swift-fse-blocks")}
								</Button>
								{fallbackImageId && (
									<Button
										onClick={() =>
											setAttributes({
												fallbackImageId: undefined,
												fallbackImageUrl: undefined,
											})
										}
										link
										isDestructive
										style={{ marginLeft: "8px" }}
									>
										{__("Remove", "swift-fse-blocks")}
									</Button>
								)}
							</div>
						)}
					/>
				</MediaUploadCheck>
				<p className="components-form-token-field__help">
					{__(
						"This image will be shown if the field is empty.",
						"swift-fse-blocks",
					)}
				</p>
			</div>
		) : (
			<TextControl
				label={__("Fallback Text", "swift-fse-blocks")}
				value={fallbackText}
				onChange={(value) => setAttributes({ fallbackText: value })}
				help={__("Show this text if the field is empty.", "swift-fse-blocks")}
			/>
		);

	return (
		<>
			<InspectorControls>
				<PanelBody title={__("ACF Field Binding", "swift-fse-blocks")}>
					{loading ? (
						<Spinner />
					) : (
						<SelectControl
							label={__("Select ACF Field", "swift-fse-blocks")}
							value={acfField}
							options={fields}
							onChange={onFieldChange}
						/>
					)}
				</PanelBody>
				{acfField && (
					<PanelBody title={__("Display Options", "swift-fse-blocks")}>
						<ToggleControl
							label={__("Show Field Label", "swift-fse-blocks")}
							checked={showLabel}
							onChange={(value) => setAttributes({ showLabel: value })}
						/>
						{showLabel && (
							<TextControl
								label={__("Custom Label", "swift-fse-blocks")}
								value={customLabel}
								onChange={(value) => setAttributes({ customLabel: value })}
								placeholder={attributes.acfFieldLabel}
							/>
						)}
						{/* ** USE THE NEW CONDITIONAL CONTROLS ** */}
						{fallbackControls}
					</PanelBody>
				)}
			</InspectorControls>

			<div {...blockProps}>
				<div className="acf-binding-preview">
					{loadingPreview ? (
						<Spinner />
					) : preview ? (
						<span dangerouslySetInnerHTML={{ __html: preview }} />
					) : (
						<em>
							{acfField
								? __("No value found.", "swift-fse-blocks")
								: __(
										"Select a field to display its value.",
										"swift-fse-blocks",
								  )}
						</em>
					)}
				</div>
			</div>
		</>
	);
}
